import { useEffect, useState } from "react";

// An unresolved manifest (empty variants, or a fetch/parse failure) is retried
// on a bounded poll: the compressor writes the variants into the manifest only
// once it finishes, so a first read can legitimately come back empty. Keep the
// window short and finite so a manifest that never resolves does not poll forever.
const MANIFEST_RETRY_MS = 3000;
const MANIFEST_MAX_ATTEMPTS = 10;

/**
 * useResolvedMedia resolves an image URL that may be a compressor manifest
 * (a `.json` file) into a directly usable image URL, on web.
 *
 * Why this exists: the media compressor does not return a direct media URL. It
 * returns a manifest URL, a JSON file listing every generated variant. Backend
 * services usually resolve it at egress, so a real image URL arrives at the FE.
 * But some payloads still carry the RAW manifest URL: realtime avatar events,
 * ad org avatars, org logos. Binding that straight into an `<img>` or an MUI
 * `<Avatar src>` renders broken until a server resolved value shows up. This
 * hook resolves it on the client so the value renders right away.
 *
 * Safe by construction: a non manifest URL is returned unchanged. A manifest
 * that has no usable variants yet (still compressing), a fetch or parse failure,
 * or a relative variant URL we cannot anchor resolves to an empty string, never
 * to the raw `.json` (which would render as a broken image). Because the
 * variants appear only once the compressor finishes, an unresolved manifest is
 * retried on a bounded poll so the image self-heals when they land.
 */

function isManifest(url: string): boolean {
  return url.endsWith(".json") || url.includes("manifest");
}

interface Variant {
  url: string;
  width?: number;
  tier?: string;
}

function pickVariantUrl(variants: Variant[]): string | null {
  if (!variants.length) return null;
  // Prefer a display optimised tier, then the widest, then the first.
  const tiered =
    variants.find((v) => v.tier === "native") ?? variants.find((v) => v.tier === "compact");
  const widest = [...variants].sort((a, b) => (b.width ?? 0) - (a.width ?? 0))[0];
  const chosen = tiered?.url || widest?.url || variants[0]?.url || null;
  // Only trust an absolute URL. A relative /media_storage path will not resolve
  // against the app origin, so keep the caller's src in that case.
  return chosen && /^https?:\/\//.test(chosen) ? chosen : null;
}

/**
 * Returns a directly bindable image URL for `src`. When `src` is a plain URL,
 * a data URL, a blob URL or empty, it is returned unchanged. When `src` is a
 * compressor manifest it is fetched and the best variant is chosen; until a
 * usable variant is found an empty string is returned so the manifest `.json`
 * never flashes as a broken image. If the manifest has no variants yet, or the
 * fetch/parse fails, the read is retried on a bounded poll so the value resolves
 * once the compressor generates the variants.
 */
export function useResolvedMedia(src: string): string {
  const [resolved, setResolved] = useState(() => (src && isManifest(src) ? "" : src));

  useEffect(() => {
    if (!src || !isManifest(src)) {
      setResolved(src);
      return;
    }
    // Blank while resolving so the manifest never flashes as a broken image.
    setResolved("");
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const scheduleRetry = () => {
      if (cancelled || attempts >= MANIFEST_MAX_ATTEMPTS) return;
      timer = setTimeout(attempt, MANIFEST_RETRY_MS);
    };

    const attempt = () => {
      attempts += 1;
      fetch(src)
        .then((r) => r.json())
        .then((m: { variants?: Variant[] }) => {
          if (cancelled) return;
          const url = pickVariantUrl(m?.variants ?? []);
          // No usable variant yet (empty/relative): keep the blank placeholder
          // instead of binding the raw `.json`, and poll until they appear.
          if (url) setResolved(url);
          else scheduleRetry();
        })
        .catch(() => {
          if (cancelled) return;
          scheduleRetry();
        });
    };

    attempt();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [src]);

  return resolved;
}
